// frontend/src/site/pages/SiteTopBar.jsx
import React, { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useSite } from '../SiteContext';

import { buttonClasses, mutedTextClasses } from '../theme/siteStyles';

const cx = (...c) => c.filter(Boolean).join(' ');

function digitsOnly(s = '') {
  return String(s).replace(/[^\d]/g, '');
}
function buildWaUrl(display) {
  const dd = digitsOnly(display || '');
  if (!dd) return '';
  const withDdi = dd.startsWith('55') ? dd : `55${dd}`;
  return `https://wa.me/${withDdi}`;
}

export default function SiteTopBar() {
  const { handle } = useParams();
  const { perfilSite, ownerWhatsUrl, theme } = useSite();

  const siteName = perfilSite?.nome || perfilSite?.siteHandle || 'Meu Site';

  const waDisplay =
    perfilSite?.whatsappDisplay ||
    perfilSite?.perfil?.whatsappDisplay ||
    perfilSite?.contatos?.whatsappDisplay ||
    '';

  // ✅ fonte única quando disponível (SiteContext já resolve certo)
  const waUrl = useMemo(() => {
    if (ownerWhatsUrl) return ownerWhatsUrl;
    const direct =
      perfilSite?.whatsappUrl ||
      perfilSite?.perfil?.whatsappUrl ||
      perfilSite?.contatos?.whatsappUrl ||
      '';
    if (direct && /^https?:\/\//i.test(direct)) return direct;
    return waDisplay ? buildWaUrl(waDisplay) : '';
  }, [ownerWhatsUrl, perfilSite, waDisplay]);

  const facebook =
    perfilSite?.facebookUrl ||
    perfilSite?.perfil?.facebookUrl ||
    perfilSite?.contatos?.facebookUrl ||
    '';

  const instagram =
    perfilSite?.instagramUrl ||
    perfilSite?.perfil?.instagramUrl ||
    perfilSite?.contatos?.instagramUrl ||
    '';

  const isDark = (theme?.mode || 'light') === 'dark';

  return (
    <div className="pointer-events-none absolute inset-x-0 top-0 z-30">
      {/* overlay sutil (neutro) */}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-28"
        style={{
          background: isDark
            ? 'linear-gradient(to bottom, rgba(0,0,0,0.55), rgba(0,0,0,0))'
            : 'linear-gradient(to bottom, rgba(15,23,42,0.22), rgba(15,23,42,0))',
        }}
        aria-hidden
      />

      <div className="pointer-events-auto max-w-7xl mx-auto px-4 pt-4">
        <div
          className={cx(
            'flex items-center justify-between',
            'rounded-full px-4 py-3',
            'backdrop-blur',
            'border border-[color:var(--border)]',
            'bg-[color:var(--card)]/70'
          )}
        >
          {/* “Logo”/nome */}
          <Link
            to={`/u/${handle}`}
            className={cx(
              'font-[family:var(--fontHeading)] font-semibold tracking-tight',
              'text-[color:var(--text)]',
              'hover:opacity-90 transition'
            )}
          >
            {siteName}
          </Link>

          {/* Ações à direita */}
          <div className="flex items-center gap-2 sm:gap-3">
            <Link
              to={`/u/${handle}/quem-sou-eu`}
              className={cx(
                buttonClasses(theme, 'secondary', '!h-9 !px-4 !text-sm'),
                'rounded-full'
              )}
            >
              Quem sou eu?
            </Link>

            {waDisplay && waUrl && (
              <a
                href={waUrl}
                target="_blank"
                rel="noreferrer"
                className={cx(
                  buttonClasses(theme, 'primary', '!h-9 !px-4 !text-sm'),
                  'rounded-full'
                )}
                aria-label="Conversar no WhatsApp"
                title="Conversar no WhatsApp"
              >
                {waDisplay}
              </a>
            )}

            {facebook && (
              <a
                href={facebook}
                target="_blank"
                rel="noreferrer"
                className={cx(
                  buttonClasses(theme, 'secondary', '!h-9 !w-9 !px-0'),
                  'rounded-full grid place-items-center'
                )}
                aria-label="Facebook"
                title="Facebook"
              >
                <span className={cx('font-semibold', mutedTextClasses())}>
                  f
                </span>
              </a>
            )}

            {instagram && (
              <a
                href={instagram}
                target="_blank"
                rel="noreferrer"
                className={cx(
                  buttonClasses(theme, 'secondary', '!h-9 !w-9 !px-0'),
                  'rounded-full grid place-items-center'
                )}
                aria-label="Instagram"
                title="Instagram"
              >
                <span className={cx('font-semibold', mutedTextClasses())}>
                  ig
                </span>
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
